// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMemo } from 'react';

/**
 * Initials avatar (in-dtt). A round chip rendered as the leading
 * glyph of the user menu — accent-tone background hashed from the
 * display name so each clinician's avatar is visually consistent
 * across sessions without storing a colour anywhere.
 *
 * The chip itself is decorative (`aria-hidden`); the surrounding
 * button carries the accessible label. Initials are derived from
 * the first letter of the first two whitespace-separated tokens
 * — common cases ("Alex Patient" → "AP", "Dr. Jane Doe" → "DJ",
 * "cher" → "C", "" → "?").
 */
export interface AvatarProps {
  displayName: string;
  /** Size in px. Defaults to 32; the sidebar button uses 28. */
  size?: number;
  /** Optional class hook for downstream layout tweaks. */
  className?: string;
}

/** Hue palette — five warm accent tones distinct enough to read at 28 px. */
const HUES: readonly { bg: string; fg: string }[] = [
  { bg: 'rgb(var(--color-primary-subtle))', fg: 'rgb(var(--color-primary))' },
  { bg: 'rgb(var(--color-accent-subtle))', fg: 'rgb(var(--color-accent))' },
  { bg: 'rgb(var(--color-info-subtle))', fg: 'rgb(var(--color-info))' },
  { bg: 'rgb(var(--color-success-subtle))', fg: 'rgb(var(--color-success))' },
  { bg: 'rgb(var(--color-warning-subtle))', fg: 'rgb(var(--color-warning))' },
];

/**
 * djb2-style hash — small, deterministic, no crypto required. Returns
 * a non-negative integer the caller can `% HUES.length` against.
 */
function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function initialsOf(displayName: string): string {
  const tokens = displayName
    .trim()
    .split(/\s+/u)
    .filter((t) => t.length > 0 && /[A-Za-zÀ-ÖØ-öø-ÿ]/u.test(t));
  if (tokens.length === 0) return '?';
  const first = tokens[0]?.[0] ?? '';
  const second = tokens.length > 1 ? (tokens[1]?.[0] ?? '') : '';
  return (first + second).toUpperCase();
}

export function Avatar({ displayName, size = 32, className }: AvatarProps) {
  const { initials, hue } = useMemo(() => {
    const safe = displayName.length > 0 ? displayName : '?';
    return {
      initials: initialsOf(safe),
      hue: HUES[hashString(safe.toLowerCase()) % HUES.length] ?? HUES[0],
    };
  }, [displayName]);

  const cls = ['avatar', className].filter(Boolean).join(' ');
  return (
    <span
      aria-hidden="true"
      data-avatar=""
      className={cls}
      style={{
        width: size,
        height: size,
        backgroundColor: hue?.bg,
        color: hue?.fg,
      }}
    >
      {initials}
    </span>
  );
}
